import { h, Fragment } from 'preact'
import { useEffect, useState } from 'preact/compat'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import ErrorMessage from '../../Common/ErrorMessage'
import Loader from '../../Common/Loader'
import { formatAsCurrency, sprintf } from '../../../helpers/common'
import PayPalLogo from '../../../assets/icons/paypal-white.svg'
import PayPalCreditLogo from '../../../assets/icons/paypal-credit-horizontal.svg'
import PayPalBlueLogo from '../../../assets/icons/PayPal_logo.svg'
import PayPalCreditBlueLogo from '../../../assets/icons/paypal_credit_horizontal.svg'
import { useTranslations } from '../../../hooks/useTranslations'
import { handlePayment } from '../../../helpers/payment'
import './styles.scss'
import useFailureRedirect from '../../../hooks/useFailureRedirect'
import useAuthoriseError from '../../../hooks/useAuthoriseError'
import InfoMessage from '../../Common/InfoMessage'
import { PAYMENT_METHOD_TYPES } from '../../../constants/paymentMethods.js'
import { useSelector } from 'react-redux'
import { pageStateSelector } from '../../../redux/slice/pageStateSlice'
import { payPalStylesSelector } from '../../../redux/slice/payPalStylesSlice'
import { transactionDetailsSelector } from '../../../redux/slice/transactionDetailsSlice'

export const PAYPAL_BUTTON_STYLES = {
  LIGHT: 'light',
  DARK: 'dark',
}

const paypalLogoMap = new Map([
  [
    PAYPAL_BUTTON_STYLES.LIGHT,
    {
      logo: PayPalBlueLogo,
      buttonClass: 'primary',
    },
  ],
  [
    PAYPAL_BUTTON_STYLES.DARK,
    {
      logo: PayPalLogo,
      buttonClass: 'secondary',
    },
  ],
])

const paypalCreditLogoMap = new Map([
  [
    PAYPAL_BUTTON_STYLES.LIGHT,
    {
      logo: PayPalCreditBlueLogo,
      buttonClass: 'primary',
    },
  ],
  [
    PAYPAL_BUTTON_STYLES.DARK,
    {
      logo: PayPalCreditLogo,
      buttonClass: 'secondary',
    },
  ],
])

function PayPal({
  children,
  handleSubmit,
  isCredit = false,
  showPaymentConfirmation,
  loaderImage,
}) {
  const l10n = useTranslations()

  const [error, setError] = useState('')
  const [isPaymentPending, setIsPaymentPending] = useState(false)
  const transactionDetails = useSelector(transactionDetailsSelector)
  const pageState = useSelector(pageStateSelector)
  const paypalStyles = useSelector(payPalStylesSelector)

  const formatAmount = formatAsCurrency(
    transactionDetails.currency,
    transactionDetails.culture,
  )

  const currentAmount = formatAmount(
    transactionDetails?.overrideAmount
      ? transactionDetails.overrideAmount
      : transactionDetails.amount,
  )

  useEffect(() => {
    if (!pageState.isPaymentBlocked) {
      setError('')
    }
  }, [pageState.isPaymentBlocked])

  useFailureRedirect()
  const handleAuthoriseError = useAuthoriseError()

  const getSelectedLogo = () => {
    const logoMap = isCredit ? paypalCreditLogoMap : paypalLogoMap
    const style = isCredit
      ? paypalStyles?.payPalCreditButton
      : paypalStyles?.payPalButton

    return (
      logoMap.get(style?.toLocaleLowerCase()) ||
      logoMap.get(PAYPAL_BUTTON_STYLES.DARK)
    )
  }

  const selectedLogo = getSelectedLogo()

  const buttonStyles = classNames('button', 'medium', selectedLogo.buttonClass)

  const paymentData = {
    pageState,
    setError,
    l10n,
    setIsPaymentPending,
    handleSubmit,
    transactionDetails,
    handleAuthoriseError,
    paymentMethod: isCredit
      ? PAYMENT_METHOD_TYPES.PayPalCredit
      : PAYMENT_METHOD_TYPES.PayPal,
  }

  return (
    <>
      {children}

      {showPaymentConfirmation &&
        (isCredit ? (
          <InfoMessage
            message={
              <>
                {sprintf(l10n.t('PAYPAL_CREDIT_MESSAGE'), currentAmount)}{' '}
                {l10n.t('Subject to status')}
                {'. '}
                <a
                  href="https://www.paypal.com/uk/webapps/mpp/paypal-virtual-credit/faq"
                  target="_blank"
                  rel="noreferrer"
                >
                  {l10n.t('Paypal Credit FAQs')}
                </a>
              </>
            }
            data-testid="paypal-credit-info-message"
            className="paypal-info-message"
          />
        ) : (
          <InfoMessage
            message={sprintf(
              l10n.t(`You're about to pay %s with PayPal`),
              currentAmount,
            )}
            data-testid="paypal-info-message"
            className="paypal-info-message"
          />
        ))}

      <ErrorMessage
        isShown={Boolean(error)}
        message={error}
        data-testid="payment-error"
      />
      {isPaymentPending ? (
        <Loader loaderImage={loaderImage} />
      ) : (
        <div className="paypal-wrapper payment-form__payment-button">
          <button
            onClick={() => handlePayment(paymentData)}
            type="button"
            data-testid="paypal-logo"
            className={buttonStyles}
          >
            <img src={selectedLogo.logo} alt={l10n.t('PayPal logo')} />
          </button>
        </div>
      )}
    </>
  )
}

PayPal.propTypes = {
  children: PropTypes.element,
  isCredit: PropTypes.bool,
  handleSubmit: PropTypes.func,
  showPaymentConfirmation: PropTypes.bool,
  loaderImage: PropTypes.string,
}

export default PayPal
